import * as moment from "moment";
import Channel from "../channel";
import { forumService } from "@/services/api";
import { iForumComment } from "@/interfaces/forum";
import ForumComment from "@/models/ForumComment";
import { iForumApiService } from "@/interfaces/services/api/forum";
import { ForumCommentCreated, ForumCommentEdited, ForumCommentUpdated } from "./broadcasts";

const BPM = 6;

export default class ForumChannelConstructor extends Channel {
    /**
     * The API service we're querying.
     */
    protected api: iForumApiService;

    /**
     * The active interval.
     */
    protected interval: number | null = null;

    /**
     * The date we last successfully completed a heartbeat.
     */
    protected _since: moment.Moment = moment.utc();

    /**
     * @param api The Forum API service.
     */
    public constructor(api: iForumApiService) {
        super();
        this.api = api;
        this.start();
    }

    /**
     * Since getter.
     */
    public get since(): moment.Moment {
        return this._since;
    }

    /**
     * A heartbeat "pulse".
     */
    public async pulse(): Promise<void> {
        const since = this.rotateSince();

        try {
            const response = await this.api.pulse(since);

            // Nothing changed.
            if (!response) {
                return Promise.resolve();
            }

            this.publishReplies(response.replies, since);

            return Promise.resolve();
        } catch (err) {
            this._since = since;
            return Promise.reject(err);
        }
    }

    /**
     * @inheritdoc
     */
    public start(): void {
        if (this.interval === null) {
            this.interval = window.setInterval(() => this.pulse(), Math.floor(60000 / BPM));
        }
    }

    /**
     * @inheritdoc
     */
    public stop(): void {
        if (this.interval !== null) {
            window.clearInterval(this.interval);
            this.interval = null;
        }
    }

    /**
     * Publishes replies to subscribers.
     * @param replies The replies to publish
     */
    protected publishReplies(replies: iForumComment[], since: moment.Moment): void {
        replies.map(reply => new ForumComment(reply)).map(reply => {
            if (since.isBefore(reply.createdAt())) {
                this.publish(ForumCommentCreated, reply);
            } else if (reply.lastEditedAt() && since.isBefore(reply.lastEditedAt())) {
                this.publish(ForumCommentEdited, reply);
            } else if (reply.updatedAt() && since.isBefore(reply.updatedAt())) {
                this.publish(ForumCommentUpdated, reply);
            }
        });
    }

    /**
     * Returns the current since value and rotates it.
     */
    protected rotateSince(): moment.Moment {
        const since = this.since;
        this._since = moment.utc();
        return since;
    }
}

export const ForumChannel = new ForumChannelConstructor(forumService);